import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Plus, UserPlus, UserMinus, CheckCircle, Clock, X, Calendar,
  Briefcase, Mail, MapPin, Users, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function OnboardingOffboardingPage() {
  const [activeTab, setActiveTab] = useState('onboarding');
  const [onboardings, setOnboardings] = useState([]);
  const [offboardings, setOffboardings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [showOffboardModal, setShowOffboardModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  const [onboardForm, setOnboardForm] = useState({
    onboarding_id: '',
    employee_name: '',
    employee_email: '',
    employee_id: '',
    department: '',
    job_title: '',
    start_date: '',
    manager: '',
    location: '',
    employment_type: 'Full-time'
  });
  
  const [offboardForm, setOffboardForm] = useState({
    offboarding_id: '',
    employee_name: '',
    employee_email: '',
    employee_id: '',
    department: '',
    job_title: '',
    last_working_day: '',
    offboarding_reason: 'Resignation',
    manager: ''
  });

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      const [onboardRes, offboardRes] = await Promise.all([
        axios.get(`${API}/onboarding`),
        axios.get(`${API}/offboarding`)
      ]);
      setOnboardings(onboardRes.data);
      setOffboardings(offboardRes.data);
    } catch (error) {
      toast.error('Failed to fetch workflows');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOnboarding = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/onboarding`, onboardForm);
      toast.success('Onboarding workflow created successfully');
      setShowOnboardModal(false);
      resetOnboardForm();
      fetchWorkflows();
    } catch (error) {
      toast.error('Failed to create onboarding workflow');
    }
  };

  const handleCreateOffboarding = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/offboarding`, offboardForm);
      toast.success('Offboarding workflow created successfully');
      setShowOffboardModal(false);
      resetOffboardForm();
      fetchWorkflows();
    } catch (error) {
      toast.error('Failed to create offboarding workflow');
    }
  };

  const handleTaskUpdate = async (workflowId, taskId, status, type) => {
    try {
      const endpoint = type === 'onboarding' ? 'onboarding' : 'offboarding';
      await axios.patch(`${API}/${endpoint}/${workflowId}/task/${taskId}`, null, {
        params: { status }
      });
      toast.success('Task updated successfully');
      fetchWorkflows();
      if (selectedWorkflow && selectedWorkflow.id === workflowId) {
        const workflows = type === 'onboarding' ? onboardings : offboardings;
        const updated = workflows.find(w => w.id === workflowId);
        setSelectedWorkflow(updated);
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDelete = async (workflowId, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type} workflow?`)) {
      return;
    }
    
    try {
      const endpoint = type === 'onboarding' ? 'onboarding' : 'offboarding';
      await axios.delete(`${API}/${endpoint}/${workflowId}`);
      toast.success(`${type} workflow deleted successfully`);
      fetchWorkflows();
      if (showDetailsModal) {
        setShowDetailsModal(false);
        setSelectedWorkflow(null);
      }
    } catch (error) {
      toast.error(`Failed to delete ${type} workflow`);
    }
  };

  const resetOnboardForm = () => {
    setOnboardForm({
      onboarding_id: '',
      employee_name: '',
      employee_email: '',
      employee_id: '',
      department: '',
      job_title: '',
      start_date: '',
      manager: '',
      location: '',
      employment_type: 'Full-time'
    });
  };

  const resetOffboardForm = () => {
    setOffboardForm({
      offboarding_id: '',
      employee_name: '',
      employee_email: '',
      employee_id: '',
      department: '',
      job_title: '',
      last_working_day: '',
      offboarding_reason: 'Resignation',
      manager: ''
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-slate-100 text-slate-800',
      'In Progress': 'bg-slate-100 text-slate-800',
      'Completed': 'bg-slate-100 text-green-800',
      'Cancelled': 'bg-slate-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTaskStatusColor = (status) => {
    const colors = {
      'Pending': 'text-slate-600',
      'In Progress': 'text-slate-600',
      'Completed': 'text-green-600'
    };
    return colors[status] || 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-slate-600">Loading workflows...</div>
      </div>
    );
  }

  const activeOnboardings = onboardings.filter(o => o.status !== 'Completed' && o.status !== 'Cancelled');
  const activeOffboardings = offboardings.filter(o => o.status !== 'Completed' && o.status !== 'Cancelled');

  return (
    <div className="space-y-6" data-testid="onboarding-offboarding-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900">
          User Lifecycle Management
        </h1>
        <p className="text-slate-600 mt-2">
          Manage employee onboarding and offboarding workflows
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-border rounded-sm p-4">
          <p className="text-sm text-slate-600">Active Onboardings</p>
          <p className="text-2xl font-heading font-semibold mt-1 text-green-600">{activeOnboardings.length}</p>
        </div>
        <div className="bg-white border border-border rounded-sm p-4">
          <p className="text-sm text-slate-600">Active Offboardings</p>
          <p className="text-2xl font-heading font-semibold mt-1 text-red-600">{activeOffboardings.length}</p>
        </div>
        <div className="bg-white border border-border rounded-sm p-4">
          <p className="text-sm text-slate-600">Completed (Total)</p>
          <p className="text-2xl font-heading font-semibold mt-1 text-slate-900">
            {onboardings.filter(o => o.status === 'Completed').length + offboardings.filter(o => o.status === 'Completed').length}
          </p>
        </div>
        <div className="bg-white border border-border rounded-sm p-4">
          <p className="text-sm text-slate-600">Pending Tasks</p>
          <p className="text-2xl font-heading font-semibold mt-1 text-slate-600">
            {[...onboardings, ...offboardings].reduce((sum, w) => sum + (w.tasks?.filter(t => t.status === 'Pending').length || 0), 0)}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('onboarding')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'onboarding'
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-700 hover:text-slate-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Onboarding ({onboardings.length})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('offboarding')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'offboarding'
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-700 hover:text-slate-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <UserMinus className="w-4 h-4" />
              Offboarding ({offboardings.length})
            </div>
          </button>
        </div>
      </div>

      {/* Content */}
      <div>
        {activeTab === 'onboarding' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button
                onClick={() => setShowOnboardModal(true)}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Onboarding
              </Button>
            </div>

            {onboardings.length === 0 ? (
              <div className="bg-white border border-border rounded-sm p-12 text-center">
                <UserPlus className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-600">No onboarding workflows yet</p>
                <Button
                  onClick={() => setShowOnboardModal(true)}
                  className="mt-4 bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                >
                  Create First Onboarding
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {onboardings.map((workflow) => (
                  <div
                    key={workflow.id}
                    className="bg-white border border-border rounded-sm p-5 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {setSelectedWorkflow(workflow); setShowDetailsModal(true);}}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-green-600" />
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${getStatusColor(workflow.status)}`}>
                          {workflow.status}
                        </span>
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">
                      {workflow.employee_name}
                    </h3>
                    
                    <div className="space-y-2 text-sm text-slate-600 mb-4">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        <span>{workflow.job_title}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span>{workflow.department}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>Starts: {new Date(workflow.start_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mb-2">
                      <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                        <span>Progress</span>
                        <span className="font-semibold">{workflow.completion_percentage}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div 
                          className="bg-slate-600 h-2 rounded-full transition-all"
                          style={{ width: `${workflow.completion_percentage}%` }}
                        />
                      </div>
                    </div>
                    
                    <p className="text-xs text-slate-700 mt-2">
                      {workflow.tasks?.filter(t => t.status === 'Completed').length || 0} / {workflow.tasks?.length || 0} tasks completed
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'offboarding' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button
                onClick={() => setShowOffboardModal(true)}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Offboarding
              </Button>
            </div>

            {offboardings.length === 0 ? (
              <div className="bg-white border border-border rounded-sm p-12 text-center">
                <UserMinus className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-600">No offboarding workflows yet</p>
                <Button
                  onClick={() => setShowOffboardModal(true)}
                  className="mt-4 bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                >
                  Create First Offboarding
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {offboardings.map((workflow) => (
                  <div
                    key={workflow.id}
                    className="bg-white border border-border rounded-sm p-5 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {setSelectedWorkflow(workflow); setShowDetailsModal(true);}}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <UserMinus className="w-5 h-5 text-red-600" />
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${getStatusColor(workflow.status)}`}>
                          {workflow.status}
                        </span>
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">
                      {workflow.employee_name}
                    </h3>
                    
                    <div className="space-y-2 text-sm text-slate-600 mb-4">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        <span>{workflow.job_title}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span>{workflow.department}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>Last Day: {new Date(workflow.last_working_day).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mb-2">
                      <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                        <span>Progress</span>
                        <span className="font-semibold">{workflow.completion_percentage}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div 
                          className="bg-slate-600 h-2 rounded-full transition-all"
                          style={{ width: `${workflow.completion_percentage}%` }}
                        />
                      </div>
                    </div>
                    
                    <p className="text-xs text-slate-700 mt-2">
                      {workflow.tasks?.filter(t => t.status === 'Completed').length || 0} / {workflow.tasks?.length || 0} tasks completed
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Onboarding Modal */}
      {showOnboardModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="text-xl font-heading font-semibold">Create Onboarding Workflow</h2>
              <button onClick={() => { setShowOnboardModal(false); resetOnboardForm(); }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateOnboarding} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="onboarding_id">Onboarding ID *</Label>
                  <Input
                    id="onboarding_id"
                    value={onboardForm.onboarding_id}
                    onChange={(e) => setOnboardForm({ ...onboardForm, onboarding_id: e.target.value })}
                    placeholder="ONB-2024-001"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="employee_name">Employee Name *</Label>
                  <Input
                    id="employee_name"
                    value={onboardForm.employee_name}
                    onChange={(e) => setOnboardForm({ ...onboardForm, employee_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="employee_email">Employee Email *</Label>
                  <Input
                    id="employee_email"
                    type="email"
                    value={onboardForm.employee_email}
                    onChange={(e) => setOnboardForm({ ...onboardForm, employee_email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="employee_id">Employee ID</Label>
                  <Input
                    id="employee_id"
                    value={onboardForm.employee_id}
                    onChange={(e) => setOnboardForm({ ...onboardForm, employee_id: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="department">Department *</Label>
                  <Input
                    id="department"
                    value={onboardForm.department}
                    onChange={(e) => setOnboardForm({ ...onboardForm, department: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="job_title">Job Title *</Label>
                  <Input
                    id="job_title"
                    value={onboardForm.job_title}
                    onChange={(e) => setOnboardForm({ ...onboardForm, job_title: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={onboardForm.start_date}
                    onChange={(e) => setOnboardForm({ ...onboardForm, start_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="manager">Manager *</Label>
                  <Input
                    id="manager"
                    value={onboardForm.manager}
                    onChange={(e) => setOnboardForm({ ...onboardForm, manager: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    value={onboardForm.location}
                    onChange={(e) => setOnboardForm({ ...onboardForm, location: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="employment_type">Employment Type *</Label>
                  <select
                    id="employment_type"
                    value={onboardForm.employment_type}
                    onChange={(e) => setOnboardForm({ ...onboardForm, employment_type: e.target.value })}
                    className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                    required
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contractor">Contractor</option>
                    <option value="Intern">Intern</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm">
                  Create Onboarding
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowOnboardModal(false); resetOnboardForm(); }}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Offboarding Modal */}
      {showOffboardModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="text-xl font-heading font-semibold">Create Offboarding Workflow</h2>
              <button onClick={() => { setShowOffboardModal(false); resetOffboardForm(); }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateOffboarding} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="offboarding_id">Offboarding ID *</Label>
                  <Input
                    id="offboarding_id"
                    value={offboardForm.offboarding_id}
                    onChange={(e) => setOffboardForm({ ...offboardForm, offboarding_id: e.target.value })}
                    placeholder="OFF-2024-001"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="off_employee_name">Employee Name *</Label>
                  <Input
                    id="off_employee_name"
                    value={offboardForm.employee_name}
                    onChange={(e) => setOffboardForm({ ...offboardForm, employee_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="off_employee_email">Employee Email *</Label>
                  <Input
                    id="off_employee_email"
                    type="email"
                    value={offboardForm.employee_email}
                    onChange={(e) => setOffboardForm({ ...offboardForm, employee_email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="off_employee_id">Employee ID</Label>
                  <Input
                    id="off_employee_id"
                    value={offboardForm.employee_id}
                    onChange={(e) => setOffboardForm({ ...offboardForm, employee_id: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="off_department">Department *</Label>
                  <Input
                    id="off_department"
                    value={offboardForm.department}
                    onChange={(e) => setOffboardForm({ ...offboardForm, department: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="off_job_title">Job Title *</Label>
                  <Input
                    id="off_job_title"
                    value={offboardForm.job_title}
                    onChange={(e) => setOffboardForm({ ...offboardForm, job_title: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="last_working_day">Last Working Day *</Label>
                  <Input
                    id="last_working_day"
                    type="date"
                    value={offboardForm.last_working_day}
                    onChange={(e) => setOffboardForm({ ...offboardForm, last_working_day: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="off_manager">Manager *</Label>
                  <Input
                    id="off_manager"
                    value={offboardForm.manager}
                    onChange={(e) => setOffboardForm({ ...offboardForm, manager: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="offboarding_reason">Offboarding Reason *</Label>
                <select
                  id="offboarding_reason"
                  value={offboardForm.offboarding_reason}
                  onChange={(e) => setOffboardForm({ ...offboardForm, offboarding_reason: e.target.value })}
                  className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                  required
                >
                  <option value="Resignation">Resignation</option>
                  <option value="Termination">Termination</option>
                  <option value="Retirement">Retirement</option>
                  <option value="Contract End">Contract End</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm">
                  Create Offboarding
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowOffboardModal(false); resetOffboardForm(); }}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedWorkflow && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="text-xl font-heading font-semibold">{selectedWorkflow.employee_name}</h2>
                <p className="text-sm text-slate-600">{selectedWorkflow.onboarding_id || selectedWorkflow.offboarding_id}</p>
              </div>
              <button onClick={() => { setShowDetailsModal(false); setSelectedWorkflow(null); }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Employee Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-sm">
                <div>
                  <p className="text-xs text-slate-600">Email</p>
                  <p className="text-sm font-medium">{selectedWorkflow.employee_email}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600">Department</p>
                  <p className="text-sm font-medium">{selectedWorkflow.department}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600">Job Title</p>
                  <p className="text-sm font-medium">{selectedWorkflow.job_title}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600">Manager</p>
                  <p className="text-sm font-medium">{selectedWorkflow.manager}</p>
                </div>
              </div>

              {/* Tasks */}
              <div>
                <h3 className="text-lg font-heading font-semibold mb-4">Tasks</h3>
                <div className="space-y-3">
                  {selectedWorkflow.tasks?.map((task) => (
                    <div key={task.task_id} className="border border-border rounded-sm p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {task.status === 'Completed' ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : task.status === 'In Progress' ? (
                              <Clock className="w-5 h-5 text-slate-600" />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-slate-600" />
                            )}
                            <h4 className="font-medium text-slate-900">{task.task_name}</h4>
                          </div>
                          <p className="text-sm text-slate-600 mb-2">{task.description}</p>
                          <p className="text-xs text-slate-700">Assigned to: {task.assigned_to}</p>
                        </div>
                        <div className="ml-4">
                          <select
                            value={task.status}
                            onChange={(e) => handleTaskUpdate(
                              selectedWorkflow.id,
                              task.task_id,
                              e.target.value,
                              selectedWorkflow.onboarding_id ? 'onboarding' : 'offboarding'
                            )}
                            className={`text-sm rounded-sm border border-input px-3 py-1 ${getTaskStatusColor(task.status)}`}
                          >
                            <option value="Pending">Pending</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={() => handleDelete(selectedWorkflow.id, selectedWorkflow.onboarding_id ? 'onboarding' : 'offboarding')}
                  variant="outline"
                  className="text-red-600 hover:bg-slate-50 rounded-sm"
                >
                  Delete Workflow
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
